// src/utils/modelLimits.js

// Default model limits (conservative estimates)
const DEFAULT_MODEL_LIMITS = {
  'openai/gpt-4o': 128000,
  'openai/gpt-4o-mini': 128000,
  'openai/gpt-4-turbo': 128000,
  'openai/gpt-3.5-turbo': 16385,
  'anthropic/claude-3-opus': 200000,
  'anthropic/claude-3-sonnet': 200000,
  'anthropic/claude-3-haiku': 200000,
  'anthropic/claude-3.5-sonnet': 200000,
  'google/gemini-1.5-pro': 2097152,
  'google/gemini-1.5-flash': 1048576,
  'google/gemini-pro': 32768,
  'meta-llama/llama-3.1-8b-instruct': 131072,
  'meta-llama/llama-3.1-8b-instruct:free': 131072,
  'microsoft/phi-3-mini-128k-instruct': 131072,
  'microsoft/phi-3-mini-128k-instruct:free': 131072,
  'google/gemma-2-9b-it': 8192,
  'google/gemma-2-9b-it:free': 8192,
  'mistralai/mistral-7b-instruct': 32768,
  'mistralai/mistral-7b-instruct:free': 32768,
  'deepseek/deepseek-r1': 131072
};

// Storage key for saved model limits
const STORAGE_KEY = 'model_limits_cache';

// Get saved model limits from localStorage
export const getSavedModelLimits = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error('Error loading saved model limits:', error);
    return {};
  }
};

// Save model limits to localStorage
export const saveModelLimit = (modelId, limit) => {
  try {
    const saved = getSavedModelLimits();
    saved[modelId] = {
      limit,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch (error) {
    console.error('Error saving model limit:', error);
  }
};

// Cache for API model data
let cachedModels = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch models from OpenRouter API (same method as ModelSelector)
const fetchModelsData = async () => {
  const now = Date.now();
  if (cachedModels && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedModels;
  }
  
  try {
    const response = await fetch('https://openrouter.ai/api/v1/models', {
      headers: {
        'HTTP-Referer': window.location.origin,
        'X-Title': 'BRIMIND App'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      cachedModels = data.data || [];
      lastFetchTime = now;
      console.log('[getModelMaxTokens] Fetched', cachedModels.length, 'models from OpenRouter API');
      return cachedModels;
    }
  } catch (error) {
    console.error('[getModelMaxTokens] Failed to fetch models from API:', error);
  }
  
  return cachedModels || [];
};

// Get the maximum tokens for a model using real API data
export const getModelMaxTokens = async (modelId) => {
  if (!modelId) return 4096;
  
  console.log('[getModelMaxTokens] Looking up context length for model:', modelId);
  
  // Check saved limits first
  const savedLimits = getSavedModelLimits();
  if (savedLimits[modelId]) {
    console.log('[getModelMaxTokens] Found saved limit:', savedLimits[modelId].limit);
    return savedLimits[modelId].limit;
  }
  
  // Try to get real data from OpenRouter API
  const models = await fetchModelsData();
  const modelData = models.find(m => m.id === modelId);
  
  if (modelData && modelData.context_length) {
    console.log('[getModelMaxTokens] Found API data for', modelId, '- context_length:', modelData.context_length);
    // Save to cache for future use
    saveModelLimit(modelId, modelData.context_length);
    return modelData.context_length;
  }
  
  // Check default limits
  if (DEFAULT_MODEL_LIMITS[modelId]) {
    console.log('[getModelMaxTokens] Found in DEFAULT_MODEL_LIMITS:', DEFAULT_MODEL_LIMITS[modelId]);
    return DEFAULT_MODEL_LIMITS[modelId];
  }
  
  // Try to match partial model names in API data
  const partialMatch = models.find(m => {
    const baseModel = m.id.split(':')[0];
    const inputBase = modelId.split(':')[0];
    return baseModel === inputBase || modelId.includes(baseModel);
  });
  
  if (partialMatch && partialMatch.context_length) {
    console.log('[getModelMaxTokens] Found partial match in API:', partialMatch.id, '- context_length:', partialMatch.context_length);
    saveModelLimit(modelId, partialMatch.context_length);
    return partialMatch.context_length;
  }
  
  // Try to match partial model names in defaults
  const modelKey = Object.keys(DEFAULT_MODEL_LIMITS).find(key => {
    const baseModel = key.split(':')[0];
    const inputBase = modelId.split(':')[0];
    return baseModel === inputBase || modelId.includes(baseModel);
  });
  
  if (modelKey) {
    console.log('[getModelMaxTokens] Found partial match in defaults:', modelKey, '->', DEFAULT_MODEL_LIMITS[modelKey]);
    return DEFAULT_MODEL_LIMITS[modelKey];
  }
  
  // Fallback based on model patterns
  const modelLower = modelId.toLowerCase();
  if (modelLower.includes('gpt-4')) {
    console.log('[getModelMaxTokens] GPT-4 pattern fallback: 128000');
    return 128000;
  }
  if (modelLower.includes('claude-3')) {
    console.log('[getModelMaxTokens] Claude-3 pattern fallback: 200000');
    return 200000;
  }
  if (modelLower.includes('gemini-1.5')) {
    console.log('[getModelMaxTokens] Gemini-1.5 pattern fallback: 1048576');
    return 1048576;
  }
  if (modelLower.includes('llama')) {
    console.log('[getModelMaxTokens] Llama pattern fallback: 131072');
    return 131072;
  }
  if (modelLower.includes('mistral')) {
    console.log('[getModelMaxTokens] Mistral pattern fallback: 32768');
    return 32768;
  }
  
  // Conservative fallback
  console.log('[getModelMaxTokens] Using conservative fallback: 4096');
  return 4096;
};

// Calculate safe max tokens for response (leave room for input)
export const getSafeMaxTokens = async (modelId, inputTokens = 1000) => {
  console.log('[getSafeMaxTokens] Starting calculation for model:', modelId, 'inputTokens:', inputTokens);
  
  const modelMax = await getModelMaxTokens(modelId);
  console.log('[getSafeMaxTokens] Model max tokens from getModelMaxTokens:', modelMax);
  
  let finalResult;
  
  // Set reasonable caps based on model type - aim for very long responses
  if (modelId.toLowerCase().includes('claude')) {
    // For Claude Sonnet 4, target around 100k tokens for responses regardless of input size
    // Claude has 200k context, so even with large inputs we can afford generous response limits
    if (inputTokens < 50000) {
      finalResult = 100000; // Full 100k if input is small
    } else if (inputTokens < 100000) {
      finalResult = 80000;  // 80k if medium input
    } else {
      finalResult = Math.max(50000, modelMax - inputTokens - 5000); // At least 50k even for large inputs
    }
    console.log('[getSafeMaxTokens] Claude model detected. Target: ~100k tokens. Final result:', finalResult);
  } else if (modelId.toLowerCase().includes('gemini')) {
    // Gemini has huge context, can be very generous
    finalResult = Math.min(150000, Math.max(50000, modelMax - inputTokens - 10000));
    console.log('[getSafeMaxTokens] Gemini model detected. Final result:', finalResult);
  } else if (modelId.toLowerCase().includes('gpt-4')) {
    // GPT-4 is more limited, be more conservative
    finalResult = Math.min(50000, Math.max(16000, modelMax - inputTokens - 5000));
    console.log('[getSafeMaxTokens] GPT-4 model detected. Final result:', finalResult);
  } else if (modelId.toLowerCase().includes('llama')) {
    finalResult = Math.min(60000, Math.max(16000, modelMax - inputTokens - 5000));
    console.log('[getSafeMaxTokens] Llama model detected. Final result:', finalResult);
  } else {
    // Default approach for unknown models
    finalResult = Math.min(30000, Math.max(8192, modelMax - inputTokens - 2000));
    console.log('[getSafeMaxTokens] Unknown model type, conservative approach. Final result:', finalResult);
  }
  
  console.log('[getSafeMaxTokens] FINAL RESULT for', modelId, ':', finalResult, 'tokens');
  console.log('[getSafeMaxTokens] ===============================================');
  
  return finalResult;
};

// Test model capability and save the result
export const testAndSaveModelLimit = async (modelId, apiKey) => {
  try {
    // Start with a small test request
    const testResponse = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'BRIMIND App'
      },
      body: JSON.stringify({
        model: modelId,
        messages: [{ role: 'user', content: 'test' }],
        max_tokens: 1
      })
    });
    
    if (testResponse.ok) {
      // Model works, use default limit
      const defaultLimit = getModelMaxTokens(modelId);
      saveModelLimit(modelId, defaultLimit);
      return defaultLimit;
    } else {
      const errorData = await testResponse.json();
      console.log('Model test failed:', errorData);
      
      // If it's a token limit error, try to extract the actual limit
      if (errorData.error?.message?.includes('maximum') || 
          errorData.error?.message?.includes('limit') ||
          errorData.error?.message?.includes('tokens')) {
        const message = errorData.error.message;
        const numberMatch = message.match(/(\d+)/g);
        if (numberMatch) {
          const extractedLimit = parseInt(numberMatch[numberMatch.length - 1]);
          if (extractedLimit > 1000 && extractedLimit < 10000000) {
            saveModelLimit(modelId, extractedLimit);
            return extractedLimit;
          }
        }
      }
    }
  } catch (error) {
    console.error('Error testing model limit:', error);
  }
  
  // Fallback to default
  return getModelMaxTokens(modelId);
};

// Handle context length errors and reset chat if needed
export const handleContextLengthError = (errorMessage, modelId, messages, setMessages, saveModelLimit) => {
  // Extract actual limit from error message
  const limitMatch = errorMessage.match(/(\d+)/);
  if (limitMatch) {
    const actualLimit = parseInt(limitMatch[0]);
    if (actualLimit > 1000) {
      saveModelLimit(modelId, actualLimit);
      
      // Reset chat with system message about the limit
      const resetMessage = {
        role: 'assistant',
        content: `⚠️ **Context Limit Reached**\n\nThis model has a maximum context length of ${actualLimit.toLocaleString()} tokens. The conversation has been reset to continue chatting.\n\nModel: ${modelId}`,
        timestamp: new Date().toISOString(),
        isSystemMessage: true
      };
      
      setMessages([resetMessage]);
      return true;
    }
  }
  
  return false;
};